/**
 * AI Chat Feature Constants
 *
 * Centralized constants for the AI chat feature to improve maintainability
 * and prevent magic numbers/strings throughout the codebase.
 */

// Error Messages
export const AI_CHAT_ERROR_MESSAGES = {
  INVALID_REQUEST:
    "I couldn't understand your request. Try breaking it into smaller steps or rephrasing it more clearly.",
  GENERIC_ERROR: "An error occurred while processing your request.",
  FETCH_FAILED: "An unexpected error occurred. Please try again.",
  NO_RETRY_REQUEST: "No previous request to retry",
} as const;

// Success Messages
export const AI_CHAT_SUCCESS_MESSAGES = {
  WORKFLOW_LOADED: (name: string) => `Loaded workflow "${name}" into canvas`,
  WORKFLOW_CREATED: (name: string) => `I've created the workflow "${name}" for you.`,
  WORKFLOW_UPDATED: (name: string) => `I've updated the workflow "${name}".`,
} as const;

// UI Constants
export const AI_CHAT_UI = {
  /** Auto-fit view duration in milliseconds */
  AUTO_FIT_DURATION: 400,
  /** Auto-fit padding */
  AUTO_FIT_PADDING: 0.2,
  /** Delay before auto-fitting view in milliseconds */
  AUTO_FIT_DELAY: 100,
} as const;

// Session ID Generation
export const SESSION_ID_PREFIX = "session_" as const;
export const MESSAGE_ID_PREFIX = "msg_" as const;

// Chat History
export const INCOMPLETE_FIELDS_HEADER = "\n\n**Before you can run this workflow, you'll need to configure:**\n";

// Input Validation
export const AI_CHAT_VALIDATION = {
  /** Maximum message length in characters */
  MAX_MESSAGE_LENGTH: 10000,
  /** Minimum message length in characters */
  MIN_MESSAGE_LENGTH: 1,
} as const;

// Timing Constants (for setTimeout delays)
export const AI_CHAT_TIMING = {
  /** Delay for scroll to bottom after panel opens */
  SCROLL_DELAY_MS: 100,
  /** Delay for quick action auto-send */
  QUICK_ACTION_DELAY_MS: 100,
  /** Delay for placeholder position updates */
  PLACEHOLDER_UPDATE_DELAY_MS: 100,
  /** Delay for node cleanup after deletion */
  NODE_CLEANUP_DELAY_MS: 50,
  /** Delay for fitView after node operations */
  FIT_VIEW_DELAY_MS: 350,
  /** Default node spacing for new nodes */
  DEFAULT_NODE_SPACING: 200,
  /** Minimum node spacing */
  MIN_NODE_SPACING: 80,
} as const;

// ============================================================================
// Streaming Constants
// ============================================================================

// Streaming API Endpoint
export const AI_CHAT_STREAM_ENDPOINT = "/v1/ai/workflow/chat/stream" as const;

// Chat History API Endpoint
export const AI_CHAT_HISTORY_ENDPOINT = "/v1/ai/workflow/chat/history" as const;

// Chat Sessions API Endpoint
export const AI_CHAT_SESSIONS_ENDPOINT = "/v1/ai/workflow/chat/sessions" as const;

// Streaming Error Messages
export const AI_CHAT_STREAM_ERROR_MESSAGES = {
  STREAM_FAILED: "Failed to connect to streaming endpoint",
  STREAM_INTERRUPTED: "Stream was interrupted unexpectedly",
  PARSE_ERROR: "Failed to parse streaming response",
  RATE_LIMITED: "Rate limit exceeded. Please try again in a moment.",
  VALIDATION_FAILED: "Workflow validation failed",
  NETWORK_ERROR: "Network connection lost. Please check your connection.",
  CONTEXT_REQUIRED: "Either sessionId or workflowId must be provided",
  CONTEXT_CONFLICT: "Cannot provide both sessionId and workflowId",
  SESSION_EXPIRED: "Your session has expired. Please refresh the page to continue.",
} as const;

// Streaming Success Messages
export const AI_CHAT_STREAM_SUCCESS_MESSAGES = {
  STREAMING_STARTED: "Generating workflow...",
  NODE_ADDED: (nodeType: string) => `Added ${nodeType} node`,
  STREAMING_COMPLETE: "Workflow generation complete!",
} as const;

// Streaming UI Constants
export const AI_CHAT_STREAM_UI = {
  /** Batch render updates interval in ms (for performance) */
  BATCH_RENDER_INTERVAL_MS: 50,
  /** Retry delay for rate limiting in ms */
  RATE_LIMIT_RETRY_DELAY_MS: 2000,
  /** Maximum retry attempts */
  MAX_RETRY_ATTEMPTS: 3,
  /** Node animation duration in ms */
  NODE_ANIMATION_DURATION_MS: 300,
} as const;

// Store Limits (to prevent memory leaks)
export const AI_CHAT_LIMITS = {
  /** Maximum number of streaming nodes to keep in memory */
  MAX_STREAMING_NODES: 100,
  /** Maximum number of loading node IDs to track */
  MAX_LOADING_NODE_IDS: 50,
  /** Maximum number of messages to keep in chat history */
  MAX_MESSAGE_HISTORY: 100,
  /** Maximum stream duration in milliseconds (5 minutes) */
  MAX_STREAM_DURATION_MS: 5 * 60 * 1000,
  /**
   * Idle timeout for SSE stream reads in milliseconds.
   * If no data (including heartbeat comments) is received for this duration,
   * the connection is considered dead and reconnection is triggered.
   * Set to 3x the backend's 30s heartbeat interval (SSEHeartbeatInterval).
   * See CS-11 in AI_AGENT_CASE_STUDIES.md for context.
   */
  SSE_IDLE_TIMEOUT_MS: 90 * 1000,
} as const;

// SSE Data Prefix
export const SSE_DATA_PREFIX = "data: " as const;

// Reconnection Constants
export const AI_CHAT_RECONNECT = {
  MAX_ATTEMPTS: 8,
  // Pre-connection retries re-POST the original message (no backend session yet).
  // Kept lower than MAX_ATTEMPTS since these are full new requests, not resumptions.
  PRE_CONNECTION_MAX_ATTEMPTS: 5,
  INITIAL_DELAY_MS: 2000,
  MAX_DELAY_MS: 15000,
  BACKOFF_MULTIPLIER: 1.5,
} as const;
